import { getTimezoneFromStorage } from './dateUtil';
import ct from 'countries-and-timezones';

/**
 * Get today's date (YYYY-MM-DD) in a specific timezone.
 * Falls back to local "today" if timezone is missing/invalid.
 */
export const getTodayInTimezone = (timezone?: string): string => {
  const tz = timezone || getTimezoneFromStorage();
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch (error) {
    console.error('Error getting today in timezone:', error);
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }
};

/**
 * Get current time components (hours, minutes) in a specific timezone.
 * Defaults to the store timezone resolved via `getTimezoneFromStorage()` (Pinia
 * storeStore with localStorage / `'UTC'` fallback) — same fallback chain as
 * `getTodayInTimezone()`. Pass `timezone` explicitly to override.
 */
export const getCurrentTimeComponentsInTimezone = (timezone?: string): { hour: number; minute: number } => {
  const tz = timezone || getTimezoneFromStorage();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const hour = Number.parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    const minute = Number.parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    return { hour, minute };
  } catch (error) {
    console.error('Error getting time components in timezone:', error);
    const now = new Date();
    return { hour: now.getHours(), minute: now.getMinutes() };
  }
};

/**
 * Get day of week name in timezone
 * @param timezone IANA timezone string
 * @returns Day name in lowercase (monday, tuesday, etc.)
 */
export const getDayOfWeekInTimezone = (timezone: string = 'UTC'): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long',
    });

    const now = new Date();
    const dayName = formatter.format(now).toLowerCase();
    return dayName;
  } catch (error) {
    console.error('Error getting day of week in timezone:', error);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayIndex = new Date().getDay();
    return days[dayIndex] || 'sunday';
  }
};

/**
 * Format time string in timezone
 * @param timezone IANA timezone string
 * @param format Format string (default: 'HH:mm')
 * @returns Formatted time string
 */
export const formatTimeInTimezone = (timezone: string = 'UTC', format: string = 'HH:mm'): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: format.includes('a') ? 'h12' : 'h23',
    });

    const now = new Date();
    return formatter.format(now);
  } catch (error) {
    console.error('Error formatting time in timezone:', error);
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }
};

/**
 * Common timezones list for dropdown (fallback if API fails)
 */
export const COMMON_TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (ICT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT/AEST)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEDT/AEST)' },
];

const getTimezoneOffsetLabel = (timezone: string, now: Date): string => {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
};

/**
 * Get all timezones from countries-and-timezones package.
 * Returns sorted array of { value, label } for select dropdowns.
 */
export const getAllTimezoneOptions = (): Array<{ value: string; label: string }> => {
  const allTimezones = ct.getAllTimezones();
  const now = new Date();
  const options = Object.values(allTimezones)
    .filter((tz) => !tz.aliasOf)
    .map((tz) => ({
      value: tz.name,
      label: `(${getTimezoneOffsetLabel(tz.name, now) || tz.utcOffsetStr}) ${tz.name.replace(/_/g, ' ')}`,
      _offset: tz.utcOffset,
    }))
    .sort((a, b) => a._offset - b._offset || a.value.localeCompare(b.value))
    .map(({ value, label }) => ({ value, label }));
  // 'UTC' is an alias of Etc/UTC in the package — inject explicitly so stored 'UTC' values match
  if (!options.some((o) => o.value === 'UTC')) {
    options.unshift({ value: 'UTC', label: '(GMT+0) UTC' });
  }
  return options;
};

/**
 * Get all countries from countries-and-timezones package.
 * Returns sorted array of { value, label } for select dropdowns.
 */
export const getAllCountryOptions = (): Array<{ value: string; label: string }> => {
  const allCountries = ct.getAllCountries();
  return Object.values(allCountries)
    .map((c) => ({
      value: c.id,
      label: c.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Get timezones for a specific country code.
 */
export const getTimezonesForCountry = (countryCode: string): Array<{ value: string; label: string }> => {
  const country = ct.getCountry(countryCode);
  if (!country) return [];
  const now = new Date();
  return country.timezones
    .map((tzName) => {
      const tz = ct.getTimezone(tzName);
      return {
        value: tzName,
        label: `(${getTimezoneOffsetLabel(tzName, now) || tz?.utcOffsetStr || ''}) ${tzName.replace(/_/g, ' ')}`,
        _offset: tz?.utcOffset ?? 0,
      };
    })
    .sort((a, b) => a._offset - b._offset || a.value.localeCompare(b.value))
    .map(({ value, label }) => ({ value, label }));
};

/**
 * Get country code from timezone name.
 */
export const getCountryFromTimezone = (timezoneName: string): string | null => {
  const tz = ct.getTimezone(timezoneName);
  if (!tz || !tz.countries || tz.countries.length === 0) return null;
  return tz.countries[0] ?? null;
};

/**
 * Normalize country value to ISO-2 code.
 * Accepts ISO-2 code ("US"), full name ("United States"), or empty string.
 * Returns uppercase ISO-2 code or original value if no match found.
 */
export const normalizeCountryCode = (country: string): string => {
  if (!country) return '';
  if (country.length === 2) return country.toUpperCase();
  const query = country.toLowerCase();
  const allCountries = Object.values(ct.getAllCountries());
  // Exact match first
  const exact = allCountries.find((c) => c.name.toLowerCase() === query);
  if (exact) return exact.id;
  // Partial: "United States" matches "United States of America"
  // Prefer shortest name match to avoid "United States Minor Outlying Islands" over "United States of America"
  const partials = allCountries
    .filter((c) => c.name.toLowerCase().startsWith(query) || c.name.toLowerCase().includes(query))
    .sort((a, b) => a.name.length - b.name.length);
  // If input is a prefix of multiple, pick the most common (shortest name that starts with query)
  const startsWith = partials.find((c) => c.name.toLowerCase().startsWith(query));
  if (startsWith) return startsWith.id;
  return partials.length > 0 ? partials[0]!.id : country;
};

